import { useRef, useState } from 'react';

import useOnClickOutsideHook from '@/hooks/useOnClickOutside';
import clsx from 'clsx';

import { Button } from '@/Components/UI/Button/Button';
import Icon from '@/Components/UI/Icon/Icon';
import PopoverV2 from '@/Components/UI/Popover/PopoverV2';
import { FILE_STATUS_PROCESSING, FILE_TYPE_EXPORTER } from '@/Utils/fileConstants';

const FileActions = ({
  dataCy = '',
  onDropdownClick,
  row,
  dropdownItems = [{ title: '', callback: () => {}, className: '' }],
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const refActionsBlock = useRef();

  const _toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleClick = (e) => {
    e.stopPropagation();
    onDropdownClick(e);
    setIsDropdownOpen(!isDropdownOpen);
  };

  useOnClickOutsideHook(isDropdownOpen, refActionsBlock, _toggleDropdown);

  return (
    <div className='relative actions-container'>
      <PopoverV2
        ref={refActionsBlock}
        open={isDropdownOpen}
        sideOffset='0'
        content={
          <div className='p-1 text-sm font-normal w-52 text-ui'>
            {dropdownItems.length > 0 ? (
              <>
                {dropdownItems.map((el, index) => (
                  <DropDownItem
                    key={index}
                    {...el}
                    dropdownItemsLength={dropdownItems.length}
                    toggleDropdown={setIsDropdownOpen}
                  />
                ))}
              </>
            ) : null}
          </div>
        }
      >
        <Button
          variant='ghost'
          className='actions-btn h-8 w-8 !px-0'
          onClick={(e) => handleClick(e)}
          data-testid={dataCy}
          data-cy='btn-file-actions'
          disabled={row.Status === FILE_STATUS_PROCESSING && row.Type === FILE_TYPE_EXPORTER}
        >
          <Icon name='dots-three-vertical' size={16} weight='bold' />
        </Button>
      </PopoverV2>
    </div>
  );
};

export default FileActions;

const DropDownItem = ({
  title,
  dataCy,
  icon,
  callback,
  premiumFeature = false,
  submenu = [],
  toggleDropdown,
}) => {
  const [isHover, setIsHover] = useState(false);

  const handleClick = (e) => {
    e.stopPropagation();
    toggleDropdown(false);
    callback();
  };

  return (
    <Button
      className={clsx(
        'relative item-template w-full h-8 items-center inline-flex !justify-start !gap-1',
        premiumFeature && '!text-ui-secondary !text-opacity-50'
      )}
      dataCy={dataCy}
      variant='ghost'
      onClick={(e) => handleClick(e)}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
      iconName={!premiumFeature ? icon : ''}
    >
      {!premiumFeature ? (
        <>{title}</>
      ) : (
        <div className='inline-flex flex-row justify-center gap-2 pt-1'>
          <Icon name={icon} size={18} color='currentcolor' />
          {title}
          <Icon name='rocket-launch' size={18} color='#D9B500' />
        </div>
      )}
      {!!submenu.length && (
        <span className='absolute top-0 bottom-0 flex items-center right-2'>
          <Icon name='caret-right' size={12} color='currentcolor' />
        </span>
      )}
      {isHover && !!submenu.length && (
        <div className='p-1 absolute -translate-x-full bottom-0 left-0 !w-64 bg-ui border border-ui-200 !rounded shadow-[5px_5px_9px_-3px_rgba(34,60,80,0.07)] flex-col flex'>
          {submenu.map(({ title, dataCy, disabled, callback, premiumFeature = false }, index) => (
            <Button
              key={index}
              className={clsx(
                'relative !h-8 !justify-start w-full !items-center',
                premiumFeature && '!text-ui-secondary !text-opacity-50 !hover:text-white',
                disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-500'
              )}
              dataCy={dataCy}
              disabled={disabled}
              onClick={disabled ? undefined : callback}
              variant='ghost'
            >
              {!premiumFeature ? (
                <>{title}</>
              ) : (
                <div className='inline-flex gap-2'>
                  {title}
                  <Icon name='rocket-launch' size={18} color='#D9B500' />
                </div>
              )}
            </Button>
          ))}
        </div>
      )}
    </Button>
  );
};
